import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Sun, Moon } from 'lucide-react';
import tokens from '../design/tokens';
import { isSampleDataCustomer } from '../services/sampleData';

/**
 * Portal shell — navy sidebar + ivory main, per design/mockups/portal.mockup.html.
 *
 * This is a restyle of the old top-header layout, not a rewrite: the same auth
 * props, the same routes, the same theme toggle (kept here per the brief, and
 * defaulting light). All four nav entries have pages: Fleet, Calls & alerts,
 * Reports, Screenings.
 */

const C = tokens.color;
const F = tokens.font;
const R = tokens.radius;
const MONO = { fontFamily: F.mono };

const SHOP_PHONE = '(423) 790-4745';

const NAV = [
  {
    key: 'fleet', label: 'Fleet', to: '/fleet',
    icon: (<><rect x="3" y="3" width="8" height="8" rx="2" /><rect x="13" y="3" width="8" height="8" rx="2" /><rect x="3" y="13" width="8" height="8" rx="2" /><rect x="13" y="13" width="8" height="8" rx="2" /></>),
  },
  {
    key: 'alerts', label: 'Calls & alerts', to: '/calls',
    icon: (<path d="M6 17V11a6 6 0 0 1 12 0v6l2 2H4z" />),
  },
  {
    key: 'reports', label: 'Reports', to: '/reports',
    icon: (<><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6" /></>),
  },
  {
    key: 'screenings', label: 'Screenings', to: '/screenings',
    icon: (<path d="M3 12h3l2-6 4 12 3-8 2 2h4" />),
  },
];

export default function PortalShell({ customerId, customerName, email, onSignOut, theme, onToggleTheme, activeNav, children }) {
  const sample = isSampleDataCustomer(customerId);
  const isDark = theme === 'dark';
  const { pathname } = useLocation();
  // activeNav: only the dev preview passes it (its URL is /__preview).
  const active = activeNav
    || (pathname.startsWith('/calls') ? 'alerts'
      : pathname.startsWith('/reports') ? 'reports'
      : pathname.startsWith('/screenings') ? 'screenings'
      : 'fleet');

  // Main area follows the theme; the sidebar is always navy (it IS the navy).
  const mainBg = isDark ? C.inkDeep : C.ground;
  const mainInk = isDark ? '#FFFFFF' : C.ink;

  return (
    <div className="bs-portal" style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '232px minmax(0,1fr)', background: mainBg, fontFamily: F.body, color: mainInk }}>
      <style>{`
        .bs-portal-main { padding: 28px 32px; }
        @media (max-width: 900px) {
          /* One column; the top bar keeps its own height and the page takes the
             rest — otherwise a short page (loading, empty fleet) stretches the
             nav bar to fill min-height: 100vh. */
          .bs-portal { grid-template-columns: minmax(0,1fr) !important; grid-template-rows: auto 1fr; }
          .bs-sidebar { flex-direction: row !important; align-items: center; gap: 16px !important; padding: 12px 16px !important; overflow-x: auto; }
          .bs-sidebar-nav { flex-direction: row !important; gap: 4px !important; }
          .bs-analyst { display: none !important; }
          /* The sidebar becomes a row here — too narrow for the wordmark. */
          .bs-brand-wordmark { display: none !important; }
          .bs-brand-icon { display: block !important; }
          .bs-portal-main { padding: 20px 16px; }
          .bs-portal-top { padding: 14px 16px 0 !important; }
        }
        @media (max-width: 560px) {
          /* Phone: the Demo account tag shares the top bar, so Sign out goes
             icon-only (it keeps its aria-label) and the name keeps its room. */
          .bs-signout-label { display: none; }
          .bs-signout { padding: 0 10px !important; }
        }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
      `}</style>

      {/* ── Sidebar ── */}
      <aside className="bs-sidebar" style={{ background: C.ink, color: '#FFFFFF', padding: '24px 18px', display: 'flex', flexDirection: 'column', gap: 28 }}>
        <Link to="/fleet" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }} aria-label="Byrdsense fleet">
          <img className="bs-brand-wordmark" src="/assets/brand/byrdsense-wordmark-reversed.svg" alt="Byrdsense" style={{ width: 160, height: 'auto', display: 'block' }} />
          <img className="bs-brand-icon" src="/assets/brand/byrdsense-icon-reversed.svg" alt="Byrdsense" style={{ width: 28, height: 'auto', display: 'none' }} />
        </Link>

        <nav className="bs-sidebar-nav" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, fontWeight: 500 }}>
          {NAV.map((item) => {
            const isActive = item.key === active;
            const inner = (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isActive ? C.accent : C.textOnDarkMuted} strokeWidth="1.8" aria-hidden="true">
                  {item.icon}
                </svg>
                {item.label}
              </>
            );
            const style = {
              textDecoration: 'none',
              color: isActive ? '#FFFFFF' : C.textOnDarkSecondary,
              background: isActive ? 'rgba(120,183,255,0.18)' : 'transparent',
              borderRadius: R.base, padding: '10px 12px',
              display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap',
            };
            return <Link key={item.key} to={item.to} style={style} aria-current={isActive ? 'page' : undefined}>{inner}</Link>;
          })}
        </nav>

        <div className="bs-analyst" style={{ marginTop: 'auto', padding: 14, borderRadius: R.lg, background: 'rgba(120,183,255,0.12)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 12, color: C.textOnDarkMuted }}>Your analyst</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Byrdsense</div>
          <a href="tel:4237904745" style={{ ...MONO, fontSize: 13, color: C.accent, textDecoration: 'none' }}>{SHOP_PHONE}</a>
        </div>
      </aside>

      {/* ── Main ── */}
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div className="bs-portal-top" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: '14px 32px 0' }}>
          {sample && (
            // Every portal screen for the sample-data customer (runtime gate on
            // customerId, never a build flag). Small and plain: a label, not a
            // reading — no status colour.
            <span role="note" className="bs-demo-tag" title="This is a demo account"
                  style={{ ...MONO, marginRight: 'auto', flexShrink: 0, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? C.textOnDarkSecondary : C.ink, border: `1px solid ${isDark ? 'rgba(255,255,255,0.25)' : C.hairlineStrong}`, borderRadius: R.full, padding: '3px 10px', whiteSpace: 'nowrap' }}>
              Demo account
            </span>
          )}
          {/* One line each — a long name ends in an ellipsis, never breaks mid-name. */}
          <div style={{ textAlign: 'right', lineHeight: 1.3, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: mainInk, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{customerName || 'Customer Portal'}</div>
            <div style={{ ...MONO, fontSize: 11, color: isDark ? C.textOnDarkMuted : C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</div>
          </div>
          <button
            onClick={onToggleTheme}
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 36, height: 36, borderRadius: R.base, border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : C.hairline}`, background: 'transparent', color: isDark ? C.textOnDarkMuted : C.textMuted, cursor: 'pointer' }}
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            onClick={onSignOut}
            aria-label="Sign out"
            className="bs-signout"
            style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap', height: 36, padding: '0 12px', borderRadius: R.base, border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : C.hairline}`, background: 'transparent', color: isDark ? C.textOnDarkMuted : C.textMuted, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: F.body }}
          >
            <LogOut className="w-4 h-4" /> <span className="bs-signout-label">Sign out</span>
          </button>
        </div>
        <main className="bs-portal-main" style={{ flex: 1, minWidth: 0 }}>{children}</main>
      </div>
    </div>
  );
}
